// Registers the service worker built from src/service-worker.js. Only runs
// in production builds — react-scripts only emits service-worker.js there.

import { isDesktopShell } from "shared/utils/isDesktopShell"

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
  window.location.hostname === "[::1]" ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
)

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // A freshly deployed SW installs but sits in "waiting" — it won't take
      // over until every open tab of the site is closed. For a looper that
      // stays open for a whole set, that could mean staying on a stale
      // version indefinitely, so surface it instead of relying on that.
      registration.onupdatefound = () => {
        const installingWorker = registration.installing
        if (!installingWorker) return
        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed" && navigator.serviceWorker.controller) {
            config?.onUpdate?.(registration)
          }
        }
      }
    })
    .catch((error) => {
      console.error("Error during service worker registration:", error)
    })
}

function checkValidServiceWorker(swUrl, config) {
  fetch(swUrl, { headers: { "Service-Worker": "script" } })
    .then((response) => {
      const contentType = response.headers.get("content-type")
      if (response.status === 404 || (contentType != null && contentType.indexOf("javascript") === -1)) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => window.location.reload())
        })
      } else {
        registerValidSW(swUrl, config)
      }
    })
    .catch(() => {
      console.log("No internet connection found. App is running in offline mode.")
    })
}

export function register(config) {
  // Electron and Tauri play the bundled mp3s straight off disk — no network
  // involved, nothing to cache — and neither's origin (file://, the Tauri
  // custom protocol) reliably supports service worker registration anyway.
  if (isDesktopShell()) return
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return

  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href)
  if (publicUrl.origin !== window.location.origin) return

  const doRegister = () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`
    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config)
    } else {
      registerValidSW(swUrl, config)
    }
  }

  // This code runs inside a React effect, which can fire after the page's
  // own 'load' event already happened (assets served instantly from local
  // cache/disk, a PWA cold-launch, etc.) — a 'load' listener attached that
  // late never fires, so the service worker silently never registers.
  // Register immediately when the page is already done loading instead.
  if (document.readyState === "complete") {
    doRegister()
  } else {
    window.addEventListener("load", doRegister)
  }
}

export function unregister() {
  if (!("serviceWorker" in navigator)) return
  navigator.serviceWorker.ready
    .then((registration) => registration.unregister())
    .catch((error) => console.error(error.message))
}
